import { BASE_URL } from "../constants";
import { getAuthHeader } from "../utils";

/**
 * @typedef ChatMessage
 * @property {number} id
 * @property {number} author
 * @property {string?} content
 * @property {string?} file
 * @property {string} timestamp
 * @property {boolean} is_by_user
 */

/**
 * Get list of chat messages for a specific chat
 * @param {object} params
 * @param {string} params.chatId - ID of the chat to fetch messages for
 * @param {number} [params.page=1] - Page number for pagination
 */
export async function getChatMessages({ chatId, page = 1 }) {
  try {
    const urlParams = new URLSearchParams();
    urlParams.append("page", page);
    urlParams.append("page_size", 30);
    if (!chatId) {
      throw new Error("Invalid chatId");
    }
    urlParams.append("chat", chatId);
    const response = await fetch(
      `${BASE_URL}insurance/chat-messages/?${urlParams.toString()}`,
      {
        headers: {
          ...getAuthHeader(),
        },
      }
    );
    if (!response.ok) {
      throw new Error("Error fetching chat messages");
    }
    const data = await response.json();
    /**@type {ChatMessage[]} */
    const results = data;
    return results;
  } catch (error) {
    console.error("Error fetching chat messages", error);
    return [];
  }
}

/**
 * Enviar mensaje de chat
 * @param {object} params
 * @param {number} params.chatId - ID of the chat to send the message to
 * @param {string} params.content - Content of the message
 * @param {string?} params.file - Optional file to send with the message
 */
export async function sendChatMessage({ chatId, content, file }) {
  try {
    if (!chatId || !content) {
      throw new Error("chatId and content are required");
    }
    const response = await fetch(`${BASE_URL}insurance/chat-messages/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...getAuthHeader(),
      },
      body: JSON.stringify({
        chat: chatId,
        content,
        file,
      }),
    });
    if (!response.ok) {
      throw new Error("Error sending chat message");
    }
    const data = await response.json();
    return { type: "success", message: "Mensaje enviado", data };
  } catch (error) {
    console.error("Error sending chat message", error);
    return { type: "error", message: error.message };
  }
}

/**
 * Obtiene los usuarios disponibles para iniciar un chat
 */
export async function getUsersForChat(userName) {
  try {
    const response = await fetch(
      `${BASE_URL}insurance/chat-users/?user_name=${userName}`,
      {
        headers: {
          ...getAuthHeader(),
        },
      }
    );
    if (!response.ok) {
      throw new Error("Error fetching chat users");
    }
    const data = await response.json();
    /**@type {User[]} */
    const results = data.results;
    return results;
  } catch (error) {
    console.error("Error fetching chat users", error);
    return [];
  }
}

/**
 * Obtiene los usuarios de soporte disponibles para el chat
 */
export async function getSupportUsersForChat() {
  try {
    const response = await fetch(`${BASE_URL}insurance/support-chat-users/`, {
      headers: {
        ...getAuthHeader(),
      },
    });
    if (!response.ok) {
      throw new Error("Error fetching support chat users");
    }
    const data = await response.json();
    /**@type {User[]} */
    const results = data;
    return results;
  } catch (error) {
    console.error("Error fetching support chat users", error);
    return [];
  }
}

/**
 * Get chats that user has available
 * @param {object} props
 * @param {number} [props.page=1] - Page number for pagination
 */
export async function getChats({ page = 1 }) {
  try {
    const urlParams = new URLSearchParams();
    urlParams.append("page", page);
    urlParams.append("page_size", 20);
    const response = await fetch(
      `${BASE_URL}insurance/chats/?${urlParams.toString()}`,
      {
        headers: {
          ...getAuthHeader(),
        },
      }
    );
    if (!response.ok) {
      throw new Error("Error fetching chats");
    }
    const data = await response.json();
    /**@type {Chat[]} */
    const results = data;
    return results;
  } catch (error) {
    console.error("Error fetching chats", error);
    return [];
  }
}

/**
 * Create a new chat
 * @param {object} params
 * @param {string} params.userId
 */
async function createChat({ userId }) {
  try {
    const response = await fetch(`${BASE_URL}insurance/chats/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...getAuthHeader(),
      },
      body: JSON.stringify({ participants: [userId] }),
    });
    if (!response.ok) {
      throw new Error("Error creating chat");
    }
    const data = await response.json();
    /**@type {Chat} */
    const chat = data;
    return chat;
  } catch (error) {
    console.error("Error creating chat", error);
    return null;
  }
}

/**
 * Creates a chat and sends a message
 * @param {object} params
 * @param {string} params.content
 * @param {File?} params.file
 * @param {string} params.userId
 */
export async function sendMessageCreatingChat({
  content = "",
  file = null,
  userId,
}) {
  try {
    const chat = await createChat({ userId });
    if (chat === null) {
      throw new Error("Error creando chat con este usuario");
    }
    const response = await sendChatMessage({
      chatId: chat.id,
      content,
      file,
    });
    if (response.type === "error") {
      throw new Error(response.message);
    }
    return {
      type: "success",
      message: "Mensaje enviado y chat creado",
      chat: chat,
      chatMessage: response.data,
    };
  } catch (error) {
    console.error("Error sending message", error);
    return { type: "error", message: error.message, chat: null };
  }
}
